import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";

const siteIcon = (color = "#84cc16") =>
  L.divIcon({
    className: "",
    html: `<div style="width:28px;height:28px;border-radius:6px;background:${color};display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,0.3);box-shadow:0 0 12px ${color}60">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0f172a" stroke-width="3" stroke-linecap="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });

const machineIcon = (status) => {
  const colors = { DEFECT: "#ef4444", TRANSPORT: "#3b82f6", UPKEEP: "#a855f7", NORMAL: "#f59e0b" };
  const c = colors[status] || colors.NORMAL;
  return L.divIcon({
    className: "",
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${c};display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,0.4);box-shadow:0 0 8px ${c}50">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M12 6V2"/></svg>
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -14],
  });
};

export default function LiveMap({ sites, machines, requests }) {
  const validSites = useMemo(() => sites.filter(s => s.gps_lat && s.gps_lon && s.gps_lat !== 0), [sites]);
  const validMachines = useMemo(() => machines.filter(m => m.gps_lat && m.gps_lon && m.gps_lat !== 0), [machines]);

  const transportRoutes = useMemo(() => {
    return requests
      .filter(r => r.request_type === "TRANSPORT" && r.status !== "DONE")
      .map(r => {
        const fromSite = sites.find(s => s.id === r.site_id);
        const toSite = sites.find(s => s.id === r.target_site_id);
        if (!fromSite?.gps_lat || !toSite?.gps_lat) return null;
        return {
          id: r.id,
          from: [fromSite.gps_lat, fromSite.gps_lon],
          to: [toSite.gps_lat, toSite.gps_lon],
          machineNumber: r.machine_number,
          fromName: fromSite.name,
          toName: toSite?.name || r.target_site_name,
        };
      })
      .filter(Boolean);
  }, [requests, sites]);

  const center = useMemo(() => {
    if (validSites.length === 0) return [50.85, 4.35];
    const avgLat = validSites.reduce((s, site) => s + site.gps_lat, 0) / validSites.length;
    const avgLon = validSites.reduce((s, site) => s + site.gps_lon, 0) / validSites.length;
    return [avgLat, avgLon];
  }, [validSites]);

  if (validSites.length === 0) {
    return (
      <div className="h-[350px] bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center" data-testid="live-map-empty">
        <p className="text-xs font-bold text-slate-400 uppercase">No GPS data available for map</p>
      </div>
    );
  }

  return (
    <div className="h-[350px] rounded-xl overflow-hidden border border-slate-200" data-testid="live-map">
      <MapContainer center={center} zoom={8} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        {validSites.map(site => (
          <Marker key={`site-${site.id}`} position={[site.gps_lat, site.gps_lon]} icon={siteIcon()}>
            <Popup>
              <div style={{ fontFamily: "system-ui", fontSize: "12px", lineHeight: "1.4" }}>
                <strong style={{ fontSize: "13px" }}>{site.name}</strong><br />
                <span style={{ color: "#94a3b8" }}>{site.city}</span><br />
                <span style={{ color: "#84cc16", fontSize: "10px" }}>
                  {machines.filter(m => m.site_id === site.id).length} machines
                </span>
              </div>
            </Popup>
          </Marker>
        ))}
        {validMachines.map(m => (
          <Marker key={`m-${m.id}`} position={[m.gps_lat, m.gps_lon]} icon={machineIcon(m.status_type)}>
            <Popup>
              <div style={{ fontFamily: "system-ui", fontSize: "12px" }}>
                <strong>{m.machine_number}</strong> - {m.name}<br />
                <span style={{ color: "#94a3b8" }}>{m.brand}</span>
                {m.status_type !== "NORMAL" && (
                  <span style={{ color: m.status_type === "DEFECT" ? "#ef4444" : "#3b82f6", marginLeft: "6px", fontWeight: 700 }}>
                    {m.status_type}
                  </span>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        {transportRoutes.map(route => (
          <Polyline
            key={route.id}
            positions={[route.from, route.to]}
            pathOptions={{ color: "#3b82f6", weight: 3, dashArray: "8 6", opacity: 0.8 }}
          >
            <Popup>
              <div style={{ fontFamily: "system-ui", fontSize: "12px" }}>
                <strong>{route.machineNumber}</strong><br />
                {route.fromName} → {route.toName}
              </div>
            </Popup>
          </Polyline>
        ))}
      </MapContainer>
    </div>
  );
}
